/**
 * Azure Speech Provider
 *
 * Implementation of IVoiceProvider for Azure Cognitive Services Speech.
 * Uses REST API for TTS/voices and WebSocket for real-time conversation.
 */

import type {
  IVoiceProvider,
  VoiceInfo,
  SynthesisOptions,
  ConversationOptions,
  AudioStream,
  HealthStatus,
  VoiceSession,
  VoiceSessionState,
  VoiceSessionEvents,
  TranscriptionResult,
} from './IVoiceProvider'

// Azure Speech specific types
interface AzureVoice {
  Name: string
  DisplayName: string
  LocalName: string
  ShortName: string
  Gender: string
  Locale: string
  LocaleName: string
  StyleList?: string[]
  VoiceType: string
  Status: string
}

interface AzureSpeechConfig {
  subscriptionKey: string
  region: string
  language?: string
}

// Azure Speech endpoints
const getTokenEndpoint = (region: string) =>
  `https://${region}.api.cognitive.microsoft.com/sts/v1.0/issueToken`
const getVoicesEndpoint = (region: string) =>
  `https://${region}.tts.speech.microsoft.com/cognitiveservices/voices/list`
const getTtsEndpoint = (region: string) =>
  `https://${region}.tts.speech.microsoft.com/cognitiveservices/v1`
const getSpeechWsEndpoint = (region: string) =>
  `wss://${region}.stt.speech.microsoft.com/speech/recognition/conversation/cognitiveservices/v1`

export class AzureSpeechProvider implements IVoiceProvider {
  readonly id = 'azure-speech'
  readonly name = 'Azure Speech'
  readonly type = 'azure' as const

  private config: AzureSpeechConfig | null = null
  private accessToken: string | null = null
  private tokenExpiry: number = 0
  private voices: VoiceInfo[] = []

  async initialize(apiKey: string): Promise<void> {
    // API key format: "subscriptionKey:region" (e.g., "abc123:eastus")
    const [subscriptionKey, region] = apiKey.split(':')
    if (!subscriptionKey || !region) {
      throw new Error('Azure Speech API key must be in format "subscriptionKey:region"')
    }

    this.config = {
      subscriptionKey,
      region,
      language: 'en-US',
    }

    // Get initial access token
    await this.refreshToken()
    // Verify by fetching voices
    await this.getVoices()
  }

  isInitialized(): boolean {
    return this.config !== null && this.accessToken !== null
  }

  private async refreshToken(): Promise<string> {
    if (!this.config) {
      throw new Error('Azure Speech provider not initialized')
    }

    // Token is valid for 10 minutes, refresh if expiring within 1 minute
    if (this.accessToken && Date.now() < this.tokenExpiry - 60000) {
      return this.accessToken
    }

    const response = await fetch(getTokenEndpoint(this.config.region), {
      method: 'POST',
      headers: {
        'Ocp-Apim-Subscription-Key': this.config.subscriptionKey,
        'Content-Length': '0',
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to get Azure token: ${response.statusText}`)
    }

    this.accessToken = await response.text()
    this.tokenExpiry = Date.now() + 9 * 60 * 1000 // 9 minutes
    return this.accessToken
  }

  async getVoices(): Promise<VoiceInfo[]> {
    if (!this.config) {
      throw new Error('Azure Speech provider not initialized')
    }

    try {
      const token = await this.refreshToken()
      const response = await fetch(getVoicesEndpoint(this.config.region), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch voices: ${response.statusText}`)
      }

      const data: AzureVoice[] = await response.json()

      // Filter to only neural voices and convert to VoiceInfo
      this.voices = data
        .filter((voice) => voice.VoiceType === 'Neural')
        .map((voice) => ({
          id: voice.ShortName,
          name: voice.DisplayName,
          description: `${voice.LocaleName} - ${voice.Gender}${voice.StyleList ? ` (${voice.StyleList.length} styles)` : ''}`,
          language: voice.Locale,
          gender: voice.Gender.toLowerCase() as 'male' | 'female' | 'neutral',
        }))

      return this.voices
    } catch (error) {
      console.error('Failed to fetch Azure voices:', error)
      throw error
    }
  }

  async createSession(
    options: ConversationOptions,
    events: VoiceSessionEvents
  ): Promise<VoiceSession> {
    if (!this.config) {
      throw new Error('Azure Speech provider not initialized')
    }

    const token = await this.refreshToken()
    return new AzureSpeechSession(
      token,
      this.config.region,
      options,
      events,
      () => this.refreshToken()
    )
  }

  async synthesize(options: SynthesisOptions): Promise<AudioStream> {
    if (!this.config) {
      throw new Error('Azure Speech provider not initialized')
    }

    const token = await this.refreshToken()

    // Build SSML for TTS
    const voiceId = options.voiceId || 'en-US-JennyNeural'
    const language = options.language || 'en-US'

    const ssml = `
      <speak version='1.0' xmlns='http://www.w3.org/2001/10/synthesis' xml:lang='${language}'>
        <voice name='${voiceId}'>
          ${escapeXml(options.text)}
        </voice>
      </speak>
    `.trim()

    const response = await fetch(getTtsEndpoint(this.config.region), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/ssml+xml',
        'X-Microsoft-OutputFormat': 'audio-24khz-96kbitrate-mono-mp3',
      },
      body: ssml,
    })

    if (!response.ok) {
      throw new Error(`Failed to synthesize speech: ${response.statusText}`)
    }

    return {
      stream: response.body!,
      mimeType: 'audio/mpeg',
      sampleRate: 24000,
    }
  }

  async healthCheck(): Promise<HealthStatus> {
    if (!this.config) {
      return {
        isHealthy: false,
        message: 'Provider not initialized',
      }
    }

    try {
      const start = Date.now()
      await this.refreshToken()
      const latency = Date.now() - start

      return {
        isHealthy: true,
        latency,
        message: 'Connected',
      }
    } catch (error) {
      return {
        isHealthy: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  async destroy(): Promise<void> {
    this.config = null
    this.accessToken = null
    this.voices = []
  }
}

/**
 * Azure Speech Session
 *
 * Manages a WebSocket connection for real-time speech recognition
 * combined with TTS for a conversational experience.
 */
class AzureSpeechSession implements VoiceSession {
  private ws: WebSocket | null = null
  private _state: VoiceSessionState = 'idle'
  private audioContext: AudioContext | null = null
  private audioQueue: AudioBuffer[] = []
  private isPlaying = false
  private currentUtterance = ''

  private token: string

  constructor(
    token: string,
    private region: string,
    private options: ConversationOptions,
    private events: VoiceSessionEvents,
    private refreshTokenFn: () => Promise<string>
  ) {
    this.token = token
  }

  get state(): VoiceSessionState {
    return this._state
  }

  private setState(newState: VoiceSessionState) {
    this._state = newState
    this.events.onStateChange?.(newState)
  }

  async start(): Promise<void> {
    if (this.ws) {
      throw new Error('Session already started')
    }

    this.setState('connecting')

    try {
      this.audioContext = new AudioContext({ sampleRate: 16000 })

      const language = this.options.language || 'en-US'
      const wsUrl = new URL(getSpeechWsEndpoint(this.region))
      wsUrl.searchParams.set('language', language)
      wsUrl.searchParams.set('format', 'detailed')
      // Add authorization token to URL (Azure supports this method)
      wsUrl.searchParams.set('authorization', `Bearer ${this.token}`)

      this.ws = new WebSocket(wsUrl.toString())

      this.ws.onopen = () => {
        // Send speech config
        const speechConfig = {
          context: {
            system: {
              name: 'SpeechSDK',
              version: '1.0.0',
              build: 'JavaScript',
            },
            os: {
              platform: 'Browser',
              name: navigator.userAgent,
              version: '',
            },
          },
        }

        // Azure Speech WebSocket uses a specific message format
        const configMessage = this.createSpeechMessage(
          'speech.config',
          'application/json',
          JSON.stringify(speechConfig)
        )
        this.ws?.send(configMessage)

        this.setState('connected')
        this.setState('listening')
      }

      this.ws.onmessage = (event) => {
        this.handleMessage(event)
      }

      this.ws.onerror = (error) => {
        console.error('Azure Speech WebSocket error:', error)
        this.setState('error')
        this.events.onError?.(new Error('WebSocket error'))
      }

      this.ws.onclose = () => {
        this.setState('disconnected')
      }
    } catch (error) {
      this.setState('error')
      throw error
    }
  }

  /**
   * Refresh the auth token for long-running sessions.
   * Azure tokens expire after 10 minutes.
   */
  async refreshToken(): Promise<void> {
    try {
      this.token = await this.refreshTokenFn()
      console.log('Azure Speech token refreshed')
    } catch (error) {
      console.error('Failed to refresh Azure token:', error)
      this.events.onError?.(new Error('Token refresh failed'))
    }
  }

  async stop(): Promise<void> {
    if (this.ws) {
      // Send telemetry end message
      const telemetryMessage = this.createSpeechMessage(
        'telemetry',
        'application/json',
        JSON.stringify({
          ReceivedMessages: {
            'turn.start': 1,
            'speech.hypothesis': 0,
            'speech.phrase': 0,
            'turn.end': 1,
          },
        })
      )
      try {
        this.ws.send(telemetryMessage)
      } catch {
        // Ignore if already closed
      }

      this.ws.close()
      this.ws = null
    }

    if (this.audioContext) {
      await this.audioContext.close()
      this.audioContext = null
    }

    this.audioQueue = []
    this.isPlaying = false
    this.setState('idle')
  }

  sendAudio(audioData: Float32Array | Int16Array): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('Cannot send audio: WebSocket not connected')
      return
    }

    // Azure expects 16-bit PCM at 16kHz
    let int16Data: Int16Array

    if (audioData instanceof Float32Array) {
      int16Data = new Int16Array(audioData.length)
      for (let i = 0; i < audioData.length; i++) {
        const s = Math.max(-1, Math.min(1, audioData[i]))
        int16Data[i] = s < 0 ? s * 0x8000 : s * 0x7fff
      }
    } else {
      int16Data = audioData
    }

    // Create audio message with header
    const audioMessage = this.createAudioMessage(int16Data.buffer as ArrayBuffer)
    this.ws.send(audioMessage)
  }

  sendText(text: string): void {
    // Azure Speech doesn't have direct text input for STT
    // This would be used to trigger TTS response instead
    console.log('Azure Speech: Text input received, would trigger TTS:', text)
    this.events.onMessage?.(text)
    this.setState('speaking')

    // In a full implementation, we'd call the TTS API here
    // and play the response audio
  }

  interrupt(): void {
    // Clear audio queue and stop playback
    this.audioQueue = []
    this.isPlaying = false

    // Send interruption signal (close current recognition turn)
    if (this.ws?.readyState === WebSocket.OPEN) {
      const endMessage = this.createSpeechMessage(
        'audio',
        'audio/x-wav',
        '' // Empty audio signals end of stream
      )
      this.ws.send(endMessage)
    }

    this.setState('listening')
  }

  private createSpeechMessage(
    path: string,
    contentType: string,
    body: string
  ): string {
    const requestId = crypto.randomUUID().replace(/-/g, '')
    const timestamp = new Date().toISOString()

    const headers = [
      `Path: ${path}`,
      `X-RequestId: ${requestId}`,
      `X-Timestamp: ${timestamp}`,
      `Content-Type: ${contentType}`,
    ].join('\r\n')

    return `${headers}\r\n\r\n${body}`
  }

  private createAudioMessage(audioData: ArrayBuffer): ArrayBuffer {
    const requestId = crypto.randomUUID().replace(/-/g, '')
    const timestamp = new Date().toISOString()

    const header = [
      'Path: audio',
      `X-RequestId: ${requestId}`,
      `X-Timestamp: ${timestamp}`,
      'Content-Type: audio/x-wav',
    ].join('\r\n') + '\r\n\r\n'

    const headerBuffer = new TextEncoder().encode(header)
    const headerLength = headerBuffer.length

    // Azure binary message format: 2-byte header length + header + body
    const message = new ArrayBuffer(2 + headerLength + audioData.byteLength)
    const view = new DataView(message)
    view.setUint16(0, headerLength, false) // Big-endian

    const messageArray = new Uint8Array(message)
    messageArray.set(headerBuffer, 2)
    messageArray.set(new Uint8Array(audioData), 2 + headerLength)

    return message
  }

  private handleMessage(event: MessageEvent): void {
    if (typeof event.data === 'string') {
      // Parse the message
      const [headerSection, body] = event.data.split('\r\n\r\n')
      const headers: Record<string, string> = {}

      headerSection.split('\r\n').forEach((line) => {
        const [key, ...valueParts] = line.split(':')
        if (key) {
          headers[key.trim()] = valueParts.join(':').trim()
        }
      })

      const path = headers['Path']

      switch (path) {
        case 'turn.start':
          this.currentUtterance = ''
          this.setState('listening')
          break

        case 'speech.hypothesis':
          try {
            const hypothesis = JSON.parse(body)
            this.currentUtterance = hypothesis.Text || hypothesis.DisplayText || ''
            const transcript: TranscriptionResult = {
              text: this.currentUtterance,
              isFinal: false,
              confidence: hypothesis.Confidence,
            }
            this.events.onTranscript?.(transcript)
          } catch {
            console.error('Failed to parse speech hypothesis')
          }
          break

        case 'speech.phrase':
          try {
            const phrase = JSON.parse(body)
            const finalText = phrase.DisplayText || phrase.Text || ''
            const transcript: TranscriptionResult = {
              text: finalText,
              isFinal: true,
              confidence: phrase.Confidence,
            }
            this.events.onTranscript?.(transcript)
            this.setState('processing')
          } catch {
            console.error('Failed to parse speech phrase')
          }
          break

        case 'turn.end':
          // Recognition turn completed
          this.setState('listening')
          break

        case 'speech.endDetected':
          // End of speech detected
          break

        default:
          console.log('Azure Speech: Unknown message path:', path)
      }
    } else if (event.data instanceof ArrayBuffer) {
      // Binary audio response (if using TTS via WebSocket)
      this.handleBinaryAudio(event.data)
    }
  }

  private async handleBinaryAudio(data: ArrayBuffer): Promise<void> {
    if (!this.audioContext) return

    try {
      // Skip the header (2-byte length + header content)
      const view = new DataView(data)
      const headerLength = view.getUint16(0, false)
      const audioData = data.slice(2 + headerLength)

      const audioBuffer = await this.audioContext.decodeAudioData(audioData)
      this.audioQueue.push(audioBuffer)

      if (!this.isPlaying) {
        this.playNextAudio()
      }
    } catch (error) {
      console.error('Failed to decode Azure audio:', error)
    }
  }

  private playNextAudio(): void {
    if (!this.audioContext || this.audioQueue.length === 0) {
      this.isPlaying = false
      this.events.onAudioEnd?.()
      this.setState('listening')
      return
    }

    this.isPlaying = true
    this.setState('speaking')
    this.events.onAudioStart?.()

    const buffer = this.audioQueue.shift()!
    const source = this.audioContext.createBufferSource()
    source.buffer = buffer
    source.connect(this.audioContext.destination)

    source.onended = () => {
      this.playNextAudio()
    }

    source.start()
  }
}

// Helper to escape XML special characters for SSML
function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

// Export singleton instance
export const azureSpeechProvider = new AzureSpeechProvider()
